// Country Pricing — per-country plan prices, in the currency the customer is
// actually charged.
//
// What this replaces, and why the layout changed
// ----------------------------------------------
// The old page was a grid of 8 hardcoded country COLUMNS holding a bare number
// each. Three things were wrong with that and none of them could be fixed
// in place:
//
//   1. No currency anywhere. Every charge is minted through the gateway, and a
//      "39" typed under "US" was charged as ₹39, not $39. The page even said
//      "the amount charged in that country's checkout" — true, and 100x wrong.
//   2. Monthly only. The yearly checkout never consulted the table at all, so a
//      US company saw a localized monthly price and a rupee yearly one.
//   3. Eight countries. Company Settings offers the full ISO list, so ~190
//      countries could be selected and none of them could be priced.
//
// Countries are ROWS now: a row carries one currency and, per plan, a monthly
// and a yearly price. That is the shape the data actually has, it scales past
// eight, and it puts the currency next to every number it governs.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Save, CheckCircle2, AlertCircle, Plus, Trash2, Globe, Info, Search,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import LoadError from "@/components/dashboard/LoadError";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { COUNTRIES } from "@/data/countries";
import { formatMoney, currencyStep } from "@/lib/money";
import { getPlans, getCountryPricing, updateCountryPricing, listBillingCurrencies } from "@/api/admin";

const COUNTRY_NAME = Object.fromEntries(COUNTRIES.filter(([c]) => c).map(([c, n]) => [c, n]));
const PERIODS = [
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" },
];

// Blank and "0" are different intents: blank REMOVES the override (fall back to
// the base list price), 0 is a price the gateway would reject. Only blank is a
// valid way to clear a cell — matched by the backend, which drops absent keys
// and refuses non-positive amounts.
const isBlank = (v) => v === "" || v === null || v === undefined;

export default function CountryPricing() {
  const [plans, setPlans] = useState([]);
  // { [country]: { currency, prices: { [planId]: { monthly, yearly } } } }
  const [rows, setRows] = useState({});
  const [baseline, setBaseline] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState("INR");
  const [hints, setHints] = useState({});
  // How many companies sit in each country, so the save confirmation can say
  // who a price change actually lands on rather than just which cells moved.
  const [companyCounts, setCompanyCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(null);

  // ---- load ---------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, cp, cur] = await Promise.all([getPlans(), getCountryPricing(), listBillingCurrencies()]);
      // /admin/plans returns the effective catalog as an OBJECT keyed by plan id.
      const planList = Array.isArray(p)
        ? p
        : (p && typeof p === "object" ? Object.entries(p).map(([id, v]) => ({ id, ...v })) : []);
      // Only PAID plans can carry an override — a free plan has no checkout to
      // apply one to, and an editable cell there is a control that does nothing.
      setPlans(planList.filter((x) => Number(x.price_inr ?? 0) > 0));

      setBaseCurrency(cur?.base || "INR");
      setCurrencies(Array.isArray(cur?.currencies) ? cur.currencies : []);
      setHints(cp?.country_currency_hints || {});
      setCompanyCounts(cp?.company_counts || {});

      // The API sends {plan: {country: {...}}}; the UI is keyed by COUNTRY,
      // because currency belongs to the country, not to the plan.
      const byCountry = {};
      Object.entries(cp?.pricing || {}).forEach(([planId, table]) => {
        Object.entries(table || {}).forEach(([code, entry]) => {
          const row = byCountry[code] || { currency: entry.currency || cur?.base || "INR", prices: {} };
          // One currency per country. Legacy rows are all INR, so this only ever
          // disagrees on data hand-edited in the database — first one wins and
          // the mismatch is visible in the cells.
          row.prices[planId] = {
            monthly: entry.monthly ?? "",
            yearly: entry.yearly ?? "",
          };
          byCountry[code] = row;
        });
      });
      setRows(byCountry);
      setBaseline(JSON.parse(JSON.stringify(byCountry)));
    } catch (e) {
      // Never substitute invented plans here: an admin could otherwise type
      // prices against plans that may not exist and press Save against a backend
      // that had just refused to talk.
      setPlans([]);
      setRows({});
      setError((e && e.message) || "Country pricing could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currencyMeta = useCallback(
    (code) => currencies.find((c) => c.code === code) || { code, exponent: 2, minimum: 0, symbol: code },
    [currencies]
  );

  // ---- edit ---------------------------------------------------------------
  function setPrice(country, planId, period, value) {
    setRows((prev) => {
      const row = { ...(prev[country] || { currency: baseCurrency, prices: {} }) };
      const prices = { ...row.prices };
      const cell = { ...(prices[planId] || { monthly: "", yearly: "" }) };
      cell[period] = value;
      prices[planId] = cell;
      return { ...prev, [country]: { ...row, prices } };
    });
  }

  function setCurrency(country, code) {
    setRows((prev) => ({ ...prev, [country]: { ...(prev[country] || { prices: {} }), currency: code } }));
  }

  function addCountry(code) {
    setRows((prev) => prev[code] ? prev : ({
      ...prev,
      // Pre-select the currency the country actually uses, so the commonest
      // path needs no thought and the wrong-currency mistake is harder to make.
      [code]: { currency: hints[code] || baseCurrency, prices: {} },
    }));
    setAddOpen(false);
    setAddQuery("");
  }

  function removeCountry(code) {
    setRows((prev) => { const next = { ...prev }; delete next[code]; return next; });
    setRemoving(null);
  }

  // ---- validation ---------------------------------------------------------
  // Mirrors the server's rules exactly (see admin_platform.CountryPriceIn), so
  // the operator is told at the keyboard rather than by a 422. The server is
  // still the control — this is only the earlier, kinder copy.
  const problems = useMemo(() => {
    const out = [];
    Object.entries(rows).forEach(([code, row]) => {
      const meta = currencyMeta(row.currency);
      const filled = [];
      plans.forEach((pl) => {
        const cell = row.prices[pl.id] || {};
        PERIODS.forEach(({ key, label }) => {
          const raw = cell[key];
          if (isBlank(raw)) return;
          const n = Number(raw);
          if (!Number.isFinite(n) || n <= 0) {
            out.push({ code, planId: pl.id, period: key,
              message: `${COUNTRY_NAME[code] || code} · ${pl.name || pl.id} · ${label}: enter a positive number, or clear it to use the ${baseCurrency} list price.` });
            return;
          }
          if (n > 10000000) {
            out.push({ code, planId: pl.id, period: key,
              message: `${COUNTRY_NAME[code] || code} · ${pl.name || pl.id} · ${label}: that price looks like a typo.` });
            return;
          }
          if (meta.minimum && n < meta.minimum) {
            out.push({ code, planId: pl.id, period: key,
              message: `${COUNTRY_NAME[code] || code} · ${pl.name || pl.id} · ${label}: below the ${formatMoney(meta.minimum, row.currency)} minimum the gateway accepts for ${row.currency}.` });
            return;
          }
          filled.push({ planId: pl.id, period: key });
        });

        // The rule that stops a customer seeing two currencies on one card: a
        // non-base currency must price BOTH periods for a plan, or the missing
        // one silently falls back to the base-currency list price.
        const hasMonthly = !isBlank(cell.monthly);
        const hasYearly = !isBlank(cell.yearly);
        if (row.currency !== baseCurrency && hasMonthly !== hasYearly) {
          out.push({ code, planId: pl.id, period: hasMonthly ? "yearly" : "monthly",
            message: `${COUNTRY_NAME[code] || code} · ${pl.name || pl.id}: a ${row.currency} price needs BOTH monthly and yearly — otherwise the missing one is charged at the ${baseCurrency} list price and the customer sees two currencies.` });
        }
      });
    });
    return out;
  }, [rows, plans, baseCurrency, currencyMeta]);

  const problemKeys = useMemo(
    () => new Set(problems.map((p) => `${p.code}/${p.planId}/${p.period}`)), [problems]);

  // ---- diff, for the confirmation ------------------------------------------
  const changes = useMemo(() => {
    const out = [];
    const codes = new Set([...Object.keys(rows), ...Object.keys(baseline)]);
    codes.forEach((code) => {
      const now = rows[code];
      const was = baseline[code];
      if (!now) { out.push({ code, kind: "removed", text: "override removed — falls back to the list price" }); return; }
      if (!was) { out.push({ code, kind: "added", text: `new override in ${now.currency}` }); return; }
      if (now.currency !== was.currency) {
        out.push({ code, kind: "changed", text: `currency ${was.currency} → ${now.currency}` });
      }
      plans.forEach((pl) => {
        PERIODS.forEach(({ key, label }) => {
          const a = (was.prices[pl.id] || {})[key] ?? "";
          const b = (now.prices[pl.id] || {})[key] ?? "";
          if (String(a) !== String(b)) {
            out.push({ code, kind: "changed",
              text: `${pl.name || pl.id} ${label.toLowerCase()}: ${isBlank(a) ? "list price" : formatMoney(a, was.currency)} → ${isBlank(b) ? "list price" : formatMoney(b, now.currency)}` });
          }
        });
      });
    });
    return out;
  }, [rows, baseline, plans]);

  const dirty = changes.length > 0;

  // Companies that will actually see a different price. Counted over the
  // DISTINCT countries in the diff — several changed cells in one country are
  // still one set of customers, and summing per row would overstate it.
  const affected = useMemo(() => {
    const codes = new Set(changes.map((c) => c.code));
    let total = 0;
    codes.forEach((code) => { total += Number(companyCounts[code] || 0); });
    return { countries: codes.size, companies: total };
  }, [changes, companyCounts]);

  // ---- save ---------------------------------------------------------------
  async function save() {
    if (busy) return;
    setBusy(true);
    setToast(null);
    try {
      // Back to the API's {plan: {country: {...}}} shape. A cell left blank is
      // OMITTED, never sent as 0 — the backend reads an absent key as "no
      // override" and a 0 as an error.
      const payload = {};
      Object.entries(rows).forEach(([code, row]) => {
        plans.forEach((pl) => {
          const cell = row.prices[pl.id] || {};
          const entry = { currency: row.currency };
          if (!isBlank(cell.monthly)) entry.monthly = Number(cell.monthly);
          if (!isBlank(cell.yearly)) entry.yearly = Number(cell.yearly);
          if (entry.monthly === undefined && entry.yearly === undefined) return;
          payload[pl.id] = payload[pl.id] || {};
          payload[pl.id][code] = entry;
        });
      });
      const res = await updateCountryPricing(payload);
      setConfirmOpen(false);
      setToast({ type: "success", message: "Country pricing saved — it applies to the next checkout." });
      // Re-read rather than trusting local state, so the baseline (and therefore
      // "unsaved changes") reflects exactly what was stored.
      await load();
      return res;
    } catch (e) {
      setToast({ type: "error", message: (e && e.message) || "Couldn't save country pricing." });
      return null;
    } finally {
      setBusy(false);
    }
  }

  const sortedCodes = useMemo(
    () => Object.keys(rows).sort((a, b) => (COUNTRY_NAME[a] || a).localeCompare(COUNTRY_NAME[b] || b)),
    [rows]
  );

  const addable = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    return COUNTRIES
      .filter(([c]) => c && !rows[c])
      .filter(([c, n]) => !q || n.toLowerCase().includes(q) || c.toLowerCase().includes(q))
      .slice(0, 80);
  }, [addQuery, rows]);

  return (
    <>
      <PageHeader
        title="Country Pricing"
        subtitle="Charge a different price, in a different currency, to companies in a given country."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} disabled={loading || !!error || !plans.length}>
              <Plus size={16} /> Add country
            </Button>
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={busy || loading || !!error || !plans.length || !dirty || problems.length > 0}
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {dirty ? `Save ${changes.length} change${changes.length === 1 ? "" : "s"}` : "Saved"}
            </Button>
          </>
        }
      />

      {toast && (
        <div className={cn("mb-4 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
          toast.type === "error"
            ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
            : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200")}>
          {toast.type === "error" ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />}
          <span className="flex-1">{toast.message}</span>
          <button type="button" onClick={() => setToast(null)}>✕</button>
        </div>
      )}

      {/* The single most important sentence on this page: every number here is
          in the row's own currency, and that is what the customer is charged. */}
      <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-brand-200/70 bg-brand-50/60 px-4 py-3 text-sm text-brand-900 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-100">
        <Info size={16} className="mt-0.5 shrink-0" />
        <div>
          <p>
            Each row is charged in <strong>its own currency</strong> — the amount you type is what the
            customer pays, in the currency shown beside it. A blank cell falls back to the{" "}
            <strong>{baseCurrency}</strong> list price from the Plans page.
          </p>
          <p className="mt-1 text-brand-800/80 dark:text-brand-200/80">
            A non-{baseCurrency} row must price <strong>both</strong> monthly and yearly, or the missing
            period would be charged at the {baseCurrency} list price and the customer would see two
            currencies on one card.
          </p>
        </div>
      </div>

      <LoadError message={error} onRetry={load} />

      {problems.length > 0 && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <p className="flex items-center gap-2 font-semibold"><AlertCircle size={15} /> Fix before saving</p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs">
            {problems.slice(0, 8).map((p, i) => <li key={i}>{p.message}</li>)}
            {problems.length > 8 && <li>…and {problems.length - 8} more.</li>}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : error ? null : plans.length === 0 ? (
        <div className="card p-8 text-center text-sm text-slate-400">No paid plans to price. Add one under Plans first.</div>
      ) : sortedCodes.length === 0 ? (
        <div className="card flex flex-col items-center gap-3 p-10 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Globe size={22} /></span>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">No country overrides yet</p>
          <p className="max-w-sm text-xs text-slate-500 dark:text-slate-400">
            Every company is charged the {baseCurrency} list price. Add a country to charge a
            different amount, in a different currency, to companies based there.
          </p>
          <Button size="sm" className="mt-1" onClick={() => setAddOpen(true)}><Plus size={15} /> Add country</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedCodes.map((code) => {
            const row = rows[code];
            const meta = currencyMeta(row.currency);
            const step = currencyStep(row.currency);
            return (
              <div key={code} className="card p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-xs font-bold text-brand-600 dark:text-brand-300">
                      {code}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800 dark:text-white">
                        {COUNTRY_NAME[code] || code}
                      </p>
                      <p className="text-xs text-slate-400">
                        Companies with this country in their Settings
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                      Charged in
                      <select
                        value={row.currency}
                        onChange={(e) => setCurrency(code, e.target.value)}
                        aria-label={`Billing currency for ${COUNTRY_NAME[code] || code}`}
                        className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-800 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
                      >
                        {currencies.map((c) => (
                          <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={() => setRemoving(code)}
                      title={`Remove ${COUNTRY_NAME[code] || code}`}
                      aria-label={`Remove ${COUNTRY_NAME[code] || code}`}
                      className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-sm">
                    <thead>
                      <tr className="text-left text-[0.6875rem] uppercase tracking-wider text-slate-400">
                        <th className="pb-2 pr-3 font-semibold">Plan</th>
                        {PERIODS.map((p) => (
                          <th key={p.key} className="pb-2 pr-3 font-semibold">{p.label} ({row.currency})</th>
                        ))}
                        <th className="pb-2 font-semibold">Customer sees</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((pl) => {
                        const cell = row.prices[pl.id] || {};
                        return (
                          <tr key={pl.id} className="border-t border-slate-100 dark:border-white/5">
                            <td className="py-2 pr-3">
                              <span className="font-medium text-slate-700 dark:text-slate-200">{pl.name || pl.id}</span>
                              <span className="ml-1.5 text-xs text-slate-400">
                                list {formatMoney(pl.price_inr, baseCurrency)}
                              </span>
                            </td>
                            {PERIODS.map(({ key, label }) => {
                              const bad = problemKeys.has(`${code}/${pl.id}/${key}`);
                              return (
                                <td key={key} className="py-2 pr-3">
                                  <div className="relative w-32">
                                    <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
                                      {meta.symbol}
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      step={step}
                                      inputMode="decimal"
                                      value={cell[key] ?? ""}
                                      onChange={(e) => setPrice(code, pl.id, key, e.target.value)}
                                      placeholder="list price"
                                      aria-label={`${pl.name || pl.id} ${label} price for ${COUNTRY_NAME[code] || code} in ${row.currency}`}
                                      aria-invalid={bad || undefined}
                                      className={cn(
                                        "h-9 w-full rounded-lg border bg-white pl-7 pr-2 text-sm tabular-nums dark:bg-white/5 dark:text-slate-100",
                                        bad
                                          ? "border-rose-400 text-rose-600 dark:border-rose-500/50"
                                          : "border-slate-200 focus:border-brand-400 dark:border-white/10"
                                      )}
                                    />
                                  </div>
                                </td>
                              );
                            })}
                            <td className="py-2 text-xs text-slate-500 dark:text-slate-400">
                              {/* Exactly what the plan card will render, built by
                                  the same formatter — so a mistyped currency is
                                  visible here before it is ever charged. */}
                              {isBlank(cell.monthly)
                                ? <span className="text-slate-400">{formatMoney(pl.price_inr, baseCurrency)}/mo · list</span>
                                : <span className="font-semibold text-slate-700 dark:text-slate-200">{formatMoney(Number(cell.monthly) || 0, row.currency)}/mo</span>}
                              {" · "}
                              {isBlank(cell.yearly)
                                ? <span className="text-slate-400">{formatMoney(pl.price_inr_yearly, baseCurrency)}/yr · list</span>
                                : <span className="font-semibold text-slate-700 dark:text-slate-200">{formatMoney(Number(cell.yearly) || 0, row.currency)}/yr</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---- add country ---- */}
      <Modal open={addOpen} onClose={() => { setAddOpen(false); setAddQuery(""); }} title="Add a country" size="md">
        <div className="space-y-3">
          <div className="relative">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
              placeholder="Search countries…"
              aria-label="Search countries"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
            />
          </div>
          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {addable.length === 0 ? (
              <li className="py-6 text-center text-sm text-slate-400">
                {addQuery ? "No country matches." : "Every country already has a row."}
              </li>
            ) : addable.map(([c, n]) => (
              <li key={c}>
                <button
                  type="button"
                  onClick={() => addCountry(c)}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-white/5"
                >
                  <span className="text-slate-700 dark:text-slate-200">{n}</span>
                  <span className="flex items-center gap-2 text-xs text-slate-400">
                    {hints[c] && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium dark:bg-white/10">{hints[c]}</span>}
                    {c}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Modal>

      {/* ---- remove country ---- */}
      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={removing ? `Remove ${COUNTRY_NAME[removing] || removing}?` : ""}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setRemoving(null)}>Keep it</Button>
            <Button size="sm" variant="danger" onClick={() => removeCountry(removing)}>Remove</Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Companies in {COUNTRY_NAME[removing] || removing} will be charged the {baseCurrency} list
          price from the next checkout. Nothing is removed until you press Save.
        </p>
      </Modal>

      {/* ---- confirm save ---- */}
      <Modal
        open={confirmOpen}
        onClose={() => (busy ? null : setConfirmOpen(false))}
        title="Apply these prices?"
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save pricing
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            This changes what real customers are charged at their next checkout. Existing
            subscriptions keep their current price until they renew or change plan.
          </p>
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
            <strong>{affected.companies}</strong>{" "}
            {affected.companies === 1 ? "company is" : "companies are"} currently in{" "}
            <strong>{affected.countries}</strong>{" "}
            {affected.countries === 1 ? "country" : "countries"} affected by this change.
          </p>
          <ul className="max-h-72 space-y-1.5 overflow-y-auto rounded-xl border border-slate-200 p-3 text-sm dark:border-white/10">
            {changes.map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={cn("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                  c.kind === "removed" ? "bg-rose-500" : c.kind === "added" ? "bg-emerald-500" : "bg-amber-500")} />
                <span className="text-slate-700 dark:text-slate-200">
                  <span className="font-semibold">{COUNTRY_NAME[c.code] || c.code}</span> — {c.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </>
  );
}
